import { createContext, type ReactNode, type SVGProps, useContext } from 'react';

import {
  type IconName,
  iconPaths,
  iconShouldMirror,
  iconUsesFill,
  type IconSet,
} from './icon-paths.js';

/**
 * The prototype icon set (the `<symbol>` sprites of each app prototype, D-48): 24×24, stroked with
 * `currentColor`. The path data is platform-neutral (`./icon-paths.ts`, shared with buyer-mobile).
 * Icons are decorative (`aria-hidden`); the control carries the name.
 */

const IconSetContext = createContext<IconSet>('panel');

/** Selects the sprite set below it: the buyer shell uses the buyer prototype's sprites. */
export function IconSetProvider({
  children,
  set,
}: {
  readonly children: ReactNode;
  readonly set: IconSet;
}) {
  return <IconSetContext.Provider value={set}>{children}</IconSetContext.Provider>;
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  readonly name: IconName;
}

export function Icon({ className, name, ...rest }: IconProps) {
  const set = useContext(IconSetContext);
  const filled = iconUsesFill(name);
  const mirror = iconShouldMirror(name);
  return (
    <svg
      aria-hidden="true"
      className={[
        'saay-icon',
        filled ? 'saay-icon--filled' : undefined,
        mirror ? 'saay-icon--mirror' : undefined,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-mirror={mirror ? '' : undefined}
      focusable="false"
      viewBox="0 0 24 24"
      {...rest}
    >
      {iconPaths(name, set).map((d) => (
        <path d={d} key={d} />
      ))}
    </svg>
  );
}
